/**
 * Team page
 * Purpose: Provide the Team page dataset and render the TeamSection with updated names,
 * positions, expertise lines and LinkedIn profile links as provided by the user.
 *
 * Notes:
 * - Adds Person JSON-LD for each team member to improve search/result snippets for people.
 */

import React from 'react'
import TeamSection, { TeamMember } from '../components/home/Team'
import Seo from '../components/Seo'

/**
 * Team dataset with updated positions, expertise and LinkedIn profile links.
 * Image URLs are kept from the original dataset; replace them if you want different headshots.
 *
 * Note:
 * - A new team member "Aram Othman Mustafa" is added right after Serhat Tepe as requested.
 * - The user will upload the image later; a smart placeholder image is used for now.
 */
const team: TeamMember[] = [
  {
    name: 'Serhat Tepe',
    position: 'Agency Manager',
    image:
      'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/b6000450-1336-44c1-a3ef-bd7f5e2943ac.png',
    linkedin: 'https://www.linkedin.com/in/serhattepe/',
    expertise: 'Traditional & Digital Advertising and Media Management – 16 Years'
  },
  {
    name: 'Aram Othman Mustafa',
    position: 'Finance and Media Buying Director',
    image: 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/59f39726-1da7-454d-8be9-1e1e0b91f3ef.jpg',
    linkedin: 'https://www.linkedin.com/in/aram-khala-9b94361b0/',
    expertise: 'Finance and Media Buying Expert – 14 Years'
  },
  {
    name: 'Mahmud A. Majid',
    position: 'Key Accounts Manager',
    image:
      'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/8b9112e5-5497-4b54-afc8-ae40ea1db4a6.jpg',
    linkedin: 'https://www.linkedin.com/in/mahmud-a-majid-a20a44210/',
    expertise: 'Advertising and Account Management – 7 Years'
  },
  {
    name: 'Ahwan Hersh Mahmood',
    position: 'International Accounts Manager',
    image:
      'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/e8c3cc60-ebe8-4f6f-9068-a54cba375f88.jpg',
    linkedin: 'https://www.linkedin.com/in/ahwan-hersh-0b5231167/',
    expertise: 'Advertising and Account Management – 9 Years'
  },
  {
    name: 'Nehla Dinç',
    position: 'Media Planning and Turquality',
    image:
      'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/c0aced60-3416-46c0-98ae-9b3a0b304c3a.jpg',
    linkedin: 'https://www.linkedin.com/in/nehla-din%C3%A7-535246229/',
    expertise: 'Media Planning & Turquality Expert – 8 Years'
  },
  {
    name: 'Zisan Basan',
    position: 'Media Planning Expert',
    image:
      'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/e8bae958-f33e-4606-95cd-19317cce0a8a.jpg',
    expertise: 'Media Planning Expert – 4 Years'
  },
  {
    name: 'Ihsan Jamal Zebari',
    position: 'Digital Media Planner',
    image:
      'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/c6261385-3504-443a-aa38-bf47a5ee92ef.jpg',
    linkedin: 'https://www.linkedin.com/in/ihsan-jamal-88b85b253/',
    expertise: 'Digital Media Expert – 2 Years'
  },
  {
    name: 'Farzin Mehdi Abdullah',
    position: 'Motion Graphic Designer',
    image:
      'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/e0ffa134-300b-4c45-bb32-f305e890713d.jpg',
    linkedin: 'https://www.linkedin.com/in/farzin-mohebbi-43292b213/',
    expertise: 'Motion Graphic Design Expert – 11 Years'
  },
  {
    name: 'Flordeliza Eleazar',
    position: 'Accounting Manager',
    image:
      'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/0d6d048c-07d5-458e-8759-09d29dcf8957.jpg',
    expertise: 'Accounting Expert – 16 Years'
  },
  {
    name: 'Chro Hazhar Hussain',
    position: 'Monitoring Manager & Media Buying Assistant',
    image:
      'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/fdd76529-a67d-4871-a67f-4797377f9c7e.jpg',
    expertise: 'Monitoring & Media Reservation Expert – 10 Years'
  },
  {
    name: 'Hawzhin Mustafa Rasul',
    position: 'Monitoring Officer and Reception',
    image:
      'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/63974f4f-882b-4bb3-8a3d-77d6521bb272.jpg',
    expertise: 'Monitoring Expert – 13 Years'
  },
  {
    name: 'Helan Hassan Omer',
    position: 'Monitoring Officer',
    image:
      'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/e526503f-ce85-4ed2-a720-2ed43cdb97a1.jpg',
    expertise: 'Monitoring Expert – 9 Years'
  },
  {
    name: 'Eman Hashm Mohamad',
    position: 'Monitoring Officer',
    image:
      'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/46752ec7-8989-4567-a4b9-18861ae34f1e.jpg',
    linkedin: 'https://www.linkedin.com/in/eman-hashm-1b0640395/',
    expertise: 'Monitoring & OOH Planning Expert – 7 Years'
  },
  {
    name: 'Rizgar Abdullah Muhammad',
    position: 'Local Accounts & Marketing Executive',
    image:
      'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/db7f9a0d-ef1d-4f4c-8380-19a56b7d07c2.jpg',
    linkedin: 'https://www.linkedin.com/in/rizgar-mala-21385932b/',
    expertise: 'Local Marketing and Account Management – 6 Years'
  },
  {
    name: 'Mina Fatah',
    position: 'Local Marketing Officer',
    image:
      'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/88e5673e-a947-4b71-a085-f826cf5fa8bd.jpg',
    linkedin: 'https://www.linkedin.com/in/mina-fatah-04888a248/',
    expertise: 'Local Marketing Expert – 2 Years'
  }
]

/**
 * Build Person JSON-LD objects for team members
 * @param members - team members array
 * @returns array of Person JSON-LD objects
 */
function buildPersonJsonLd(members: TeamMember[]) {
  return members.map((m) => {
    const person: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: m.name,
      jobTitle: m.position,
      worksFor: {
        '@type': 'Organization',
        name: 'Edicoo Media and Digital Marketing Agency'
      }
    }
    if (m.image) person.image = m.image
    if ((m as any).linkedin) person.sameAs = [(m as any).linkedin]
    return person
  })
}

/**
 * Page component rendering the TeamSection with explicit visibility and compact layout.
 * Includes Person JSON-LD for team members.
 *
 * @returns JSX.Element
 */
export default function TeamPage(): JSX.Element {
  const personJsonLd = buildPersonJsonLd(team)

  return (
    <>
      <Seo
        title="Our Team — Edicoo"
        description="Meet the Edicoo leadership and specialist teams across media planning, digital marketing and creative services."
        url="https://your-site.com/team"
        image="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/f2dcbeba-4716-4434-8b8b-01acce34f085.png"
        jsonLd={personJsonLd}
      />
      <TeamSection
        items={team}
        title="Our Agency Team"
        forceVisible
        size="compact"
        showImageUpload={true}
      />
    </>
  )
}